import { lazy, useCallback, useEffect, memo, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'

import Bugsnag from '@bugsnag/js'

import { observer } from 'mobx-react'

import AbortDetector from 'AbortDetector'
import Heartbeat from 'Heartbeat'
import IdleDetector from 'containers/IdleDetector'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { startApp } from 'startup'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { useStores } from 'stores/utils/hooks/useStores'
import {
  setGlobalEventProperty,
  trackInAmplitude
} from 'utils/tracking/amplitude'
import {
  AmplitudeEvent,
  EventProperty
} from 'utils/tracking/amplitude/amplitude.interface'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { ErrorProvider } from 'context/Error'
import { UserProvider } from 'context/User'
import { StylesProvider } from '@mui/styles'
import { StyledEngineProvider } from '@mui/material/styles'
import { generateClassName } from 'theme/material'
import Routes from './Routes'
import LoadingProvider, { LoadingMode } from './components/LoadingProvider'
import { isDevMenuEnabled } from './utils/env'
import ProgressProvider from './ProgressContext'
import ThemeProvider from './ThemeProvider'
import getExperimentVariation from 'utils/tracking/optimizely/getExperimentVariation'
import { Experiments } from 'CustomOptimizelyProvider'
import TopBanner from 'components/AbTestComponents/CAT4360/TopBanner'
import VoucherActivatedBanner from 'components/AbTestComponents/CAT4360/VoucherActivatedBanner'
import useVoucherAmount from 'components/AbTestComponents/CAT4360/useVoucherAmount'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { scrollToElement } from 'utils/pollard/scroll'

/**
 * We load the container lazily here
 * because we don't want it to be loaded in the production bundle
 */
const DevContainer = isDevMenuEnabled()
  ? lazy(() => import('./devTools/DevContainer'))
  : () => null

const Metadata = observer(() => {
  const { traversal } = useStores()
  const {
    traversalId,
    data: {
      system: { advertisementId }
    }
  } = traversal

  useEffect(() => {
    setGlobalEventProperty(EventProperty.TraversalId, traversalId)

    Bugsnag.addMetadata('traversal', {
      id: traversalId,
      advertisementId
    })
  }, [traversalId, advertisementId])

  return null
})

const App = () => {
  const {
    rootStore: {
      experimentData,
      resumeDetailsFetched,
      navigation: { parentUrl }
    },
    rootStore
  } = useRootStore()

  const urlParams = useUrlParams()
  const navigate = useNavigate()

  const boot = useCallback(() => {
    rootStore.setLoadingMode(LoadingMode.Initial)

    try {
      trackInAmplitude(AmplitudeEvent.OnTaurineStartsLoading, {
        timestamp: Number(new Date())
      })
      startApp(rootStore, navigate, urlParams)
    } catch (error) {
      if (error instanceof Error) {
        logAndTrackError(error)
      }
    } finally {
      rootStore.setLoadingMode(LoadingMode.None)
    }
  }, [rootStore, urlParams, navigate])

  useEffect(() => {
    boot()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // eslint-disable react-hooks/exhaustive-deps
    if (!parentUrl) {
      return
    }
    /* using utm campaign here because of these tests are for ecrm */

    getExperimentVariation(
      Experiments.CAT_4463_RCE_DAC_SKIP_BUTTON_SALABLE,
      rootStore,
      {
        utm_campaign: urlParams.utm_campaign || '',
        taurine_parent_url: parentUrl || ''
      }
    )

    getExperimentVariation(
      Experiments.CAT_4463_RCE_DAC_SKIP_BUTTON_ACCEPTED,
      rootStore,
      {
        utm_campaign: urlParams.utm_campaign || '',
        taurine_parent_url: parentUrl || ''
      }
    )

    /* using utm campaign here because of these tests are for ecrm */

    getExperimentVariation(Experiments.CAT_4359_DAC_SKIP_BUTTON_V3, rootStore, {
      utm_campaign: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    getExperimentVariation(Experiments.CAT_4360_AB_VOUCHER_TEST, rootStore, {
      utm_source: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    // KILL SWITCHES
    getExperimentVariation(Experiments.CAT_3494_DAC_KILL_SWITCH, rootStore, {
      utm_source: urlParams.utm_source || ''
    })

    getExperimentVariation(
      Experiments.CAT_3614_CAPTCHA_KILL_SWITCH,
      rootStore,
      {
        utm_source: urlParams.utm_source || ''
      }
    )
    // AA tests
    getExperimentVariation(Experiments.CAT_4251_AA_TEST_NEW_EVENT, rootStore, {
      utm_source: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    getExperimentVariation(Experiments.CAT_4478_DAC_SKIP_BUTTON_V4, rootStore, {
      utm_source: urlParams.utm_source || '',
      taurine_parent_url: parentUrl || ''
    })

    // add experiment here
  }, [resumeDetailsFetched, parentUrl])

  /*
    This is useEffect for calling an Amplitude event: OnTaurineBecomesUsable.
    If `.MuiSkeleton-pulse` exists in DOM/body that means inputs are still loading.
    When they are all gone, it means Taurine if fully interactive.
   */
  useEffect(() => {
    const callback = (
      mutationsList: MutationRecord[],
      bodyObserver: MutationObserver
    ) => {
      for (const mutationList of mutationsList) {
        if (mutationList.removedNodes.length > 0) {
          const skeletonPulse = document.querySelector('.MuiSkeleton-pulse')

          if (!skeletonPulse) {
            trackInAmplitude(AmplitudeEvent.OnTaurineBecomesUsable, {
              timestamp: Number(new Date())
            })
            bodyObserver.disconnect()
            break
          }
        }
      }
    }

    const bodyObserver = new MutationObserver(callback)
    const body = document.querySelector('body')
    const config = { childList: true, subtree: true }

    if (body) {
      bodyObserver.observe(body, config)
    }

    return () => {
      bodyObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isInVoucherExperiment =
    // @ts-ignore - experimentData type needs refinement for dynamic access
    experimentData[Experiments.CAT_4360_AB_VOUCHER_TEST] ===
    PossibleVariants.VARIATION_B

  const {
    traversalSession,
    traversal: {
      data: {
        common: { amount }
      }
    },
    navigation: { currentPageInSession }
  } = useStores()
  const voucherAmount = useVoucherAmount()
  const topBannerTitle =
    voucherAmount != null
      ? `${voucherAmount} € Gutschein erhalten!`
      : 'Gutschein erhalten!'

  return (
    <StyledEngineProvider injectFirst>
      {isInVoucherExperiment &&
        currentPageInSession === 'contact' &&
        amount >= 20000 && (
          <TopBanner
            title={topBannerTitle}
            subtitle="Nur bei smava."
            onClick={() => {
              const el = document.getElementById('cat4360-bottom-banner')
              if (!el) return
              const isSafari = /^((?!chrome|android).)*safari/i.test(
                navigator.userAgent
              )
              if (isSafari) {
                scrollToElement(el, rootStore.isInIframe)
              } else {
                requestAnimationFrame(() => {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                })
              }
            }}
          />
        )}
      {isInVoucherExperiment &&
        currentPageInSession !== 'contact' &&
        amount >= 20000 &&
        traversalSession.isVoucherActivated && <VoucherActivatedBanner />}
      <ThemeProvider>
        <StylesProvider generateClassName={generateClassName}>
          <ProgressProvider>
            <UserProvider>
              <ErrorProvider>
                <LoadingProvider>
                  <Routes navigate={navigate} />
                </LoadingProvider>
              </ErrorProvider>
              <IdleDetector />
            </UserProvider>
            <Metadata />
            <Heartbeat />
            <AbortDetector />
            <Suspense fallback={null}>
              <DevContainer />
            </Suspense>
          </ProgressProvider>
        </StylesProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default memo(observer(App))
